<template>

    <div class="ui-pagination" v-if="resolvedPages > 0">
        <ul>
            <li>
                <a href="#" @click.prevent="go(-1)"><i class="bx bx-chevron-left"></i></a>
            </li>
            <li v-if="stickyLimits && rangeStart > 1">
                <a href="#" :class="{ active: activePage == 1 }" @click.prevent="switchPage(1)">1</a>
            </li>
            <li v-if="stickyLimits && rangeStart > 2">
                <a href="#" @click.prevent>...</a>
            </li>
            <li v-for="pageItem in visibleRange">
                <a href="#" :class="{ active: activePage == pageItem }" @click.prevent="switchPage(pageItem)">{{ pageItem }}</a>
            </li>
            <li v-if="stickyLimits && rangeEnd < resolvedPages - 1">
                <a href="#" @click.prevent>...</a>
            </li>
            <li v-if="stickyLimits && rangeEnd < resolvedPages">
                <a href="#" :class="{ active: activePage == resolvedPages }" @click.prevent="switchPage(resolvedPages)">{{ resolvedPages }}</a>
            </li>
            <li>
                <a href="#" @click.prevent="go(1)"><i class="bx bx-chevron-right"></i></a>
            </li>
        </ul>
        <span class="rowcount" v-if="showTotal">
            Totaal {{ $n(total) }} {{ total != 1 ? 'items' : 'item' }}
        </span>
    </div>

</template>

<script lang="ts">

import { Component, Prop, Vue, Watch } from 'vue-facing-decorator';
import { first, last, range } from 'lodash'

import type { ComponentProps } from '@wittignl/js-core';
import { createComponentMixin } from '@wittignl/js-core';

@Component({
    name: 'ui-pagination',
    emits: ['update:page', 'update:offset']
})
export default class Pagination extends Vue {

    @Prop({ type: Number })
    pages: number;

    @Prop({ type: Number })
    page: number;

    @Prop({ type: Number })
    total: number;

    @Prop({ type: Number })
    limit: number;

    @Prop({ type: Number })
    offset: number;

    @Prop({ type: Boolean })
    showTotal: boolean;

    @Prop({ type: Number, default: 11 })
    maxVisible: number;

    @Prop({ type: Boolean, default: false })
    stickyLimits: boolean;

    activePage = 1;

    created() {

        this.updateActivePage();
    }

    @Watch('page')
    @Watch('pages')
    @Watch('offset')
    @Watch('total')
    @Watch('limit')
    updateActivePage() {

        if(this.page){

            this.activePage = Math.min(this.page, this.resolvedPages);
        }
        else if(this.limit && this.limit > 0){

            this.activePage = Math.floor((this.offset || 0) / this.limit) + 1
        }
        else {

            this.activePage = 1;
        }
    }

    switchPage(page) {

        this.activePage = page;
        this.$emit('update:page', page);

        const offset = this.limit * (page - 1);
        this.$emit('update:offset', offset);
    }

    go(direction) {

        const page = Math.max(Math.min(this.activePage + direction, this.resolvedPages), 1);
        this.switchPage(page);
    }

    get visibleRange() {
        if (this.resolvedPages <= this.maxVisible) {
            return range(1, this.resolvedPages + 1);
        }

        if (!this.stickyLimits) {
            const halfVisible = Math.floor(this.maxVisible / 2);
            let start = Math.max(1, this.activePage - halfVisible);
            let end = Math.min(this.resolvedPages, start + this.maxVisible - 1);
            
            if (end - start + 1 < this.maxVisible) {
                start = Math.max(1, end - this.maxVisible + 1);
            }
            
            return range(start, end + 1);
        }

        const sideWidth = Math.floor((this.maxVisible - 2) / 2);
        let start = this.activePage - sideWidth;
        let end = this.activePage + sideWidth;

        if (start <= 1) {
            end = Math.min(this.maxVisible - 1, this.resolvedPages - 1);
            start = 2;
        } else if (end >= this.resolvedPages) {
            start = Math.max(2, this.resolvedPages - (this.maxVisible - 2));
            end = this.resolvedPages - 1;
        }

        return range(start, end + 1);
    }

    get resolvedPages() {

        if(this.pages){

            return this.pages;
        }
        else if(this.total && this.limit){

            return Math.ceil(this.total / this.limit);
        }
        else {

            return 0;
        }
    }

    get rangeStart() {

        return first(this.visibleRange);
    }

    get rangeEnd() {

        return last(this.visibleRange);
    }
}

export type PaginationComp = InstanceType<typeof Pagination>
export type PaginationProps = ComponentProps<PaginationComp>
export const paginationCompMixin = createComponentMixin(Pagination)

</script>
<style src="../../../scss/component/navigation/Pagination.scss" lang="scss"></style>